import type { Answers } from './types';
import { getPassport } from './types';

type Lang = 'en' | 'ru';

export interface PersonaChip {
  /** Stable id — used as React key. */
  id: string;
  /** Single character / short emoji shown left of the label. */
  icon: string;
  /** Plain text label (already localized). */
  label: string;
  /** Optional dimension hint — used to colour-key chips against the bar. */
  hint?: 'climate' | 'cost' | 'visa' | 'tax' | 'family' | 'language' | 'jobs' | 'banking' | 'priority';
}

const BUDGET_LABEL: Record<number, { en: string; ru: string }> = {
  1: { en: '< €500/mo', ru: 'до €500/мес' },
  2: { en: '€500–1,000/mo', ru: '€500–1 000/мес' },
  3: { en: '€1,000–2,000/mo', ru: '€1 000–2 000/мес' },
  4: { en: '€2,000+/mo', ru: 'от €2 000/мес' },
};

const CLIMATE_LABEL: Record<string, { en: string; ru: string }> = {
  alps: { en: 'Mountains / Alps', ru: 'Горы / Альпы' },
  mediterranean: { en: 'Mediterranean', ru: 'Средиземноморье' },
  continental: { en: 'Four seasons', ru: 'Четыре сезона' },
  tropical: { en: 'Tropical', ru: 'Тропики' },
  northern: { en: 'Northern', ru: 'Север' },
  any: { en: 'Any climate', ru: 'Любой климат' },
};

const URBAN_LABEL: Record<string, { en: string; ru: string }> = {
  capital: { en: 'Capitals', ru: 'Столицы' },
  mid_city: { en: 'Mid-size cities', ru: 'Средние города' },
  town: { en: 'Small towns', ru: 'Малые города' },
  coastal: { en: 'Coastal', ru: 'Побережье' },
};

const INCOME_LABEL: Record<string, { en: string; ru: string }> = {
  remote: { en: 'Remote work', ru: 'Удалённая работа' },
  local: { en: 'Local job', ru: 'Местная работа' },
  business: { en: 'Own business', ru: 'Свой бизнес' },
  passive: { en: 'Passive income', ru: 'Пассивный доход' },
};

const FAMILY_LABEL: Record<string, { en: string; ru: string }> = {
  solo: { en: 'Solo', ru: 'Один' },
  partner: { en: 'With partner', ru: 'С партнёром' },
  'kids-preschool': { en: 'Kids (preschool)', ru: 'Дети (дошкольники)' },
  'kids-school': { en: 'Kids (school)', ru: 'Дети (школьники)' },
  pets: { en: 'With pets', ru: 'С питомцами' },
};

const PRIORITY_LABEL: Record<string, { en: string; ru: string }> = {
  safety: { en: 'Safety', ru: 'Безопасность' },
  healthcare: { en: 'Healthcare', ru: 'Медицина' },
  schools: { en: 'Schools', ru: 'Школы' },
  transport: { en: 'Transport', ru: 'Транспорт' },
  nature: { en: 'Nature', ru: 'Природа' },
  culture: { en: 'Culture', ru: 'Культура' },
  'ru-community': { en: 'Russian community', ru: 'Русскоязычное сообщество' },
  sports: { en: 'Active lifestyle', ru: 'Активный образ жизни' },
};

const DEALBREAKER_LABEL: Record<string, { en: string; ru: string }> = {
  'hot-summer': { en: 'No hot summers', ru: 'Без летней жары' },
  snow: { en: 'No snowy winters', ru: 'Без снежной зимы' },
  humidity: { en: 'Low humidity', ru: 'Без сырости' },
  earthquakes: { en: 'No earthquakes', ru: 'Без землетрясений' },
  'complex-visa': { en: 'Simple visa path', ru: 'Простая виза' },
  'expensive-healthcare': { en: 'Affordable healthcare', ru: 'Доступная медицина' },
};

const PASSPORT_LABEL: Record<string, { en: string; ru: string }> = {
  eu: { en: 'EU passport', ru: 'Паспорт ЕС' },
  anglo: { en: 'Anglo passport', ru: 'Англо-паспорт' },
  ru: { en: 'RU passport', ru: 'Паспорт РФ' },
  by: { en: 'BY passport', ru: 'Паспорт РБ' },
  ua: { en: 'UA passport', ru: 'Паспорт Украины' },
  other: { en: 'Other passport', ru: 'Другой паспорт' },
};

const LANGUAGE_LABEL: Record<string, { en: string; ru: string }> = {
  en: { en: 'English', ru: 'Английский' },
  it: { en: 'Italian', ru: 'Итальянский' },
  es: { en: 'Spanish', ru: 'Испанский' },
  de: { en: 'German', ru: 'Немецкий' },
  fr: { en: 'French', ru: 'Французский' },
  pt: { en: 'Portuguese', ru: 'Португальский' },
  any: { en: 'Willing to learn', ru: 'Готов учить' },
  'ru-only': { en: 'Russian only', ru: 'Только русский' },
};

const HORIZON_LABEL: Record<string, { en: string; ru: string }> = {
  now: { en: 'Moving now', ru: 'Переезжаю сейчас' },
  soon: { en: 'Moving within a year', ru: 'В течение года' },
  next_year: { en: 'Moving in 12–24 mo', ru: 'Через 12–24 мес' },
  explore: { en: 'Exploring options', ru: 'Изучаю варианты' },
  already: { en: 'Already relocated', ru: 'Уже переехал' },
};

const VISA_PATHWAY_LABEL: Record<string, { en: string; ru: string }> = {
  digital_nomad: { en: 'DN visa', ru: 'DN-виза' },
  freelance_self_employed: { en: 'Freelance', ru: 'Фриланс' },
  golden_investor: { en: 'Golden visa', ru: 'Golden visa' },
  family_reunion: { en: 'Family reunion', ru: 'Семейное воссоединение' },
  ancestry: { en: 'Ancestry', ru: 'По происхождению' },
  work_sponsor: { en: 'Work sponsor', ru: 'Рабочий permit' },
  student_to_perm: { en: 'Student → perm', ru: 'Студент → ВНЖ' },
  refugee_protection: { en: 'Refugee / protection', ru: 'Беженство' },
};

const INCOME_AMOUNT_LABEL: Record<string, { en: string; ru: string }> = {
  lt1500: { en: '< €1.5k/mo', ru: 'до €1.5к/мес' },
  '1500to3000': { en: '€1.5–3k/mo', ru: '€1.5–3к/мес' },
  '3000to6000': { en: '€3–6k/mo', ru: '€3–6к/мес' },
  gt6000: { en: '€6k+/mo', ru: 'от €6к/мес' },
};

const TAX_RESIDENCY_LABEL: Record<string, { en: string; ru: string }> = {
  break_origin: { en: 'Breaking origin tax residency', ru: 'Уйти из origin резидентства' },
  keep_origin: { en: 'Keeping origin tax residency', ru: 'Остаюсь в origin' },
  dual: { en: 'May end up dual resident', ru: 'Возможно дуальный резидент' },
  treaty_minimize: { en: 'Treaty-minimised', ru: 'Через DTT-договор' },
};

function pick(map: Record<string, { en: string; ru: string }>, id: string, lang: Lang): string | null {
  const entry = map[id];
  return entry ? entry[lang] : null;
}

/**
 * Build a compact, scannable list of "what you asked for" chips from quiz answers.
 * Used at the top of the quiz result page so the user immediately sees what
 * inputs drove the ranking — addresses the "every user sees the same words"
 * complaint by making the personalization visible BEFORE the per-country cards.
 */
export function buildPersonaSummary(answers: Answers, lang: Lang): PersonaChip[] {
  const chips: PersonaChip[] = [];

  // 1. Horizon (only when actionable — "now" / "next_year" / "already")
  const horizon = answers.horizon?.optionIds[0];
  if (horizon && (horizon === 'now' || horizon === 'next_year' || horizon === 'already')) {
    const label = pick(HORIZON_LABEL, horizon, lang);
    if (label) chips.push({ id: `horizon-${horizon}`, icon: '🕒', label });
  }

  // 1b. Visa pathway — show up to 2 picked (most informative)
  const visas = answers.visa_pathway?.optionIds ?? [];
  for (const v of visas.slice(0, 2)) {
    const label = pick(VISA_PATHWAY_LABEL, v, lang);
    if (label) chips.push({ id: `visa-${v}`, icon: '🛂', label, hint: 'visa' });
  }

  // 2. Climate (1–2)
  const climates = answers.climate?.optionIds ?? [];
  for (const c of climates.slice(0, 2)) {
    const label = pick(CLIMATE_LABEL, c, lang);
    if (label) chips.push({ id: `climate-${c}`, icon: '🌤', label, hint: 'climate' });
  }

  // 3. Urban density
  const densities = answers.urban_density?.optionIds ?? [];
  for (const d of densities.slice(0, 2)) {
    const label = pick(URBAN_LABEL, d, lang);
    if (label) chips.push({ id: `urban-${d}`, icon: '🏘', label });
  }

  // 4. Budget bucket — show as a single chip with the human range
  const bucket = answers.budget?.budgetBucket;
  if (bucket && BUDGET_LABEL[bucket]) {
    chips.push({
      id: `budget-${bucket}`,
      icon: '€',
      label: BUDGET_LABEL[bucket][lang],
      hint: 'cost',
    });
  }

  // 5. Income type(s)
  const incomes = answers.income?.optionIds ?? [];
  for (const i of incomes.slice(0, 2)) {
    const label = pick(INCOME_LABEL, i, lang);
    if (label) chips.push({ id: `income-${i}`, icon: '💼', label, hint: i === 'business' || i === 'passive' ? 'tax' : 'jobs' });
  }

  // 5b. Income amount bucket
  const incomeAmount = answers.income_amount?.optionIds[0];
  if (incomeAmount) {
    const label = pick(INCOME_AMOUNT_LABEL, incomeAmount, lang);
    if (label) chips.push({ id: `income-amount-${incomeAmount}`, icon: '💶', label, hint: 'cost' });
  }

  // 5c. Tax residency stance (skip 'unsure' — not informative)
  const taxIntent = answers.tax_residency_intent?.optionIds[0];
  if (taxIntent && taxIntent !== 'unsure') {
    const label = pick(TAX_RESIDENCY_LABEL, taxIntent, lang);
    if (label) chips.push({ id: `tax-${taxIntent}`, icon: '🧾', label, hint: 'tax' });
  }

  // 6. Family configuration (skip solo as it's the default-feeling option)
  const family = answers.family?.optionIds ?? [];
  const familyShown = family.filter((id) => id !== 'solo');
  for (const f of familyShown.slice(0, 2)) {
    const label = pick(FAMILY_LABEL, f, lang);
    if (label) chips.push({ id: `family-${f}`, icon: f.startsWith('kids') ? '👶' : f === 'pets' ? '🐾' : '👫', label, hint: f.startsWith('kids') ? 'family' : undefined });
  }

  // 7. Top 2 priorities
  const priorities = answers.priorities?.optionIds ?? [];
  for (const p of priorities.slice(0, 2)) {
    const label = pick(PRIORITY_LABEL, p, lang);
    if (label) chips.push({ id: `prio-${p}`, icon: '★', label, hint: 'priority' });
  }

  // 8. Languages spoken / willing
  const languages = answers.languages?.optionIds ?? [];
  // Skip plain "en" + "any" unless they're the only signal
  const langsShown = languages.filter((l) => l !== 'en' && l !== 'any');
  for (const l of langsShown.slice(0, 2)) {
    const label = pick(LANGUAGE_LABEL, l, lang);
    if (label) chips.push({ id: `lang-${l}`, icon: '🗣', label, hint: 'language' });
  }

  // 9. Passport (only RU/BY/UA — others don't materially affect ranking)
  const passport = getPassport(answers);
  if (passport && (passport === 'ru' || passport === 'by' || passport === 'ua')) {
    const label = pick(PASSPORT_LABEL, passport, lang);
    if (label) chips.push({ id: `passport-${passport}`, icon: '🛂', label, hint: 'banking' });
  }

  // 10. Dealbreakers — these are constraints, visually distinct (⛔)
  const dealbreakers = answers.dealbreakers?.optionIds ?? [];
  for (const d of dealbreakers.slice(0, 3)) {
    const label = pick(DEALBREAKER_LABEL, d, lang);
    if (label) chips.push({ id: `nope-${d}`, icon: '⛔', label });
  }

  // 11. Planning to buy — only surface YES (no/unsure are baseline)
  const buyProperty = answers.buy_property?.optionIds[0];
  if (buyProperty === 'yes') {
    chips.push({
      id: 'buy-property-yes',
      icon: '🏠',
      label: lang === 'ru' ? 'Планирует покупку' : 'Planning to buy',
      hint: 'cost',
    });
  }

  // 12. Existing ties — show up to 2 named countries, then "+N more"
  const NAMES_EN: Record<string, string> = {
    AE: 'UAE', AL: 'Albania', AR: 'Argentina', BR: 'Brazil', CO: 'Colombia',
    CR: 'Costa Rica', CY: 'Cyprus', EE: 'Estonia', ES: 'Spain', GE: 'Georgia',
    GR: 'Greece', HR: 'Croatia', HU: 'Hungary', ID: 'Indonesia', IT: 'Italy',
    ME: 'Montenegro', MT: 'Malta', MU: 'Mauritius', MX: 'Mexico', MY: 'Malaysia',
    PA: 'Panama', PT: 'Portugal', PY: 'Paraguay', RS: 'Serbia', SG: 'Singapore',
    TH: 'Thailand', TR: 'Turkey', UY: 'Uruguay', VN: 'Vietnam', ZA: 'South Africa',
  };
  const NAMES_RU: Record<string, string> = {
    AE: 'ОАЭ', AL: 'Албания', AR: 'Аргентина', BR: 'Бразилия', CO: 'Колумбия',
    CR: 'Коста-Рика', CY: 'Кипр', EE: 'Эстония', ES: 'Испания', GE: 'Грузия',
    GR: 'Греция', HR: 'Хорватия', HU: 'Венгрия', ID: 'Индонезия', IT: 'Италия',
    ME: 'Черногория', MT: 'Мальта', MU: 'Маврикий', MX: 'Мексика', MY: 'Малайзия',
    PA: 'Панама', PT: 'Португалия', PY: 'Парагвай', RS: 'Сербия', SG: 'Сингапур',
    TH: 'Таиланд', TR: 'Турция', UY: 'Уругвай', VN: 'Вьетнам', ZA: 'ЮАР',
  };
  const tiedCountries = answers.ties?.optionIds ?? [];
  if (tiedCountries.length > 0) {
    const names = lang === 'ru' ? NAMES_RU : NAMES_EN;
    const shown = tiedCountries.slice(0, 2).map((c) => names[c] ?? c).join(', ');
    const extra = tiedCountries.length > 2 ? ` +${tiedCountries.length - 2}` : '';
    chips.push({
      id: 'ties-countries',
      icon: '🤝',
      label: shown + extra,
    });
  }

  return chips;
}

/**
 * Score-gap commentary for the top-3 podium: tells the user whether #1 is a
 * clear winner or if the top results are essentially tied. Returns null if
 * there are fewer than 2 ranked matches.
 */
export function describeTopGap(
  topMatches: { score: number }[],
  lang: Lang,
): { tone: 'tight' | 'close' | 'clear'; label: string } | null {
  if (topMatches.length < 2) return null;
  const gap12 = topMatches[0].score - topMatches[1].score;
  const gap13 = topMatches.length >= 3 ? topMatches[0].score - topMatches[2].score : gap12;

  // Tight: top-2 within 3 percentage points → "essentially tied"
  if (gap12 < 0.03) {
    return {
      tone: 'tight',
      label:
        lang === 'ru'
          ? 'Топ-2 практически равны — выбор за вами.'
          : 'Top two are essentially tied — your call.',
    };
  }
  // Close: top-3 within 6 percentage points
  if (gap13 < 0.06) {
    return {
      tone: 'close',
      label:
        lang === 'ru'
          ? 'Топ-3 близки по подходящести — все три заслуживают рассмотрения.'
          : 'Top three are close — all three are worth a serious look.',
    };
  }
  return {
    tone: 'clear',
    label:
      lang === 'ru'
        ? 'Лидер чётко впереди.'
        : 'A clear leader.',
  };
}
